import { useMemo } from "react";
import { autoUpdate } from "@floating-ui/react";
import { useNavigationContext } from "@/providers/NavigationProvider";
import { useEscapePortalContainer } from "@/providers/EscapePortalContainer";

// Popover props that let a phone dropdown use the whole viewport. Undefined off phone layout.
// Don't add sizeOptions here — that replaces the library's apply and its maxHeight wiring.
export const usePhoneEscapePopoverProps = () => {
  const { isPhoneLayout } = useNavigationContext();
  const escapeContainer = useEscapePortalContainer();

  return useMemo(() => {
    if (!isPhoneLayout || !escapeContainer) return undefined;

    return {
      options: {
        // fixed = measured against visualViewport, i.e. the space above the keyboard.
        strategy: "fixed" as const,
        // iOS fires only visualViewport events on keyboard open, which autoUpdate ignores.
        whileElementsMounted: (
          reference: Parameters<typeof autoUpdate>[0],
          floating: Parameters<typeof autoUpdate>[1],
          update: Parameters<typeof autoUpdate>[2]
        ) => {
          const stopAutoUpdate = autoUpdate(reference, floating, update);
          const viewport = window.visualViewport;
          viewport?.addEventListener("resize", update);
          viewport?.addEventListener("scroll", update);

          return () => {
            stopAutoUpdate();
            viewport?.removeEventListener("resize", update);
            viewport?.removeEventListener("scroll", update);
          };
        }
      },
      floatingProps: { portalProps: { container: escapeContainer } }
    };
  }, [isPhoneLayout, escapeContainer]);
};
